import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppShell from '../components/layout/AppShell';
import PaymentGateway from '../components/PaymentGateway';
import PaymentReceipt from '../components/payments/PaymentReceipt';
import { useStudyPlans } from '../hooks/useStudyPlans';
import {
  GED_EXAM_SUBJECTS,
  formatUsd,
  computeExamCheckoutTotal,
  computeDegreeCheckoutTotal,
} from '../data/studyPlans';
import {
  buildDegreeBreakdown,
  buildExamBreakdown,
  categoryLabel,
  createPaymentRecord,
  filterAndSortPayments,
  formatPaymentDate,
  modeLabel,
  subscribeStudentPayments,
} from '../utils/payments';
import { getAppNavItems } from '../utils/appNavItems';
import { SBA } from '../theme';

export default function PaymentsPage() {
  const { currentUser, isAdmin, hasSubscription } = useAuth();
  const { examFee, degreeFee, adminContactPhone, loading: pricingLoading } = useStudyPlans();
  const navigate = useNavigate();
  const location = useLocation();

  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [examSearch, setExamSearch] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [examMode, setExamMode] = useState('card');
  const [degreeMode, setDegreeMode] = useState('card');
  const [checkout, setCheckout] = useState(null); // { kind: 'exam'|'degree', ... }
  const [receiptPayment, setReceiptPayment] = useState(null);
  const [contactMsg, setContactMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const navItems = getAppNavItems(isAdmin, location.pathname, { hasSubscription });

  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    return subscribeStudentPayments(currentUser.uid, setPayments);
  }, [currentUser?.uid]);

  const allSorted = useMemo(() => filterAndSortPayments(payments, search), [payments, search]);
  const examPayments = useMemo(
    () => filterAndSortPayments(payments.filter((p) => p.category === 'exam'), examSearch),
    [payments, examSearch]
  );

  const examTotals = useMemo(
    () => computeExamCheckoutTotal(selectedSubjects.length, examFee),
    [selectedSubjects, examFee]
  );
  const degreeTotals = useMemo(() => computeDegreeCheckoutTotal(degreeFee), [degreeFee]);

  function toggleSubject(id) {
    setSelectedSubjects((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setContactMsg('');
  }

  function handleExamContinue() {
    setContactMsg('');
    if (!selectedSubjects.length) {
      setContactMsg('Select at least one GED exam subject.');
      return;
    }
    if (examMode === 'card') {
      const breakdown = buildExamBreakdown(selectedSubjects, examFee);
      setCheckout({
        kind: 'exam',
        subjects: [...selectedSubjects],
        amount: breakdown.total,
        breakdown,
        label: `GED Exam Fee: ${selectedSubjects
          .map((id) => GED_EXAM_SUBJECTS.find((s) => s.id === id)?.name || id)
          .join(', ')}`,
      });
      return;
    }
    setContactMsg(
      `Please contact the administration team to pay by ${
        examMode === 'online' ? 'online banking' : 'cash'
      }. Call ${adminContactPhone}.`
    );
  }

  function handleDegreeContinue() {
    setContactMsg('');
    if (degreeMode === 'card') {
      const breakdown = buildDegreeBreakdown(degreeFee);
      setCheckout({
        kind: 'degree',
        amount: breakdown.total,
        breakdown,
        label: 'GED Diploma & Transcript',
      });
      return;
    }
    setContactMsg(
      `Please contact the administration team to pay diploma/transcript charges by ${
        degreeMode === 'online' ? 'online banking' : 'cash'
      }. Call ${adminContactPhone}.`
    );
  }

  async function handleCheckoutPay() {
    if (!checkout || !currentUser) return;
    setBusy(true);
    try {
      const payment = await createPaymentRecord({
        userId: currentUser.uid,
        category: checkout.kind,
        label: checkout.label,
        amount: checkout.amount,
        breakdown: checkout.breakdown,
        mode: 'hosted-bank',
        status: 'paid',
        subjects: checkout.subjects || [],
        createdBy: 'student-hosted',
        createdByUid: currentUser.uid,
      });
      const planLabel = checkout.label;
      setCheckout(null);
      setSelectedSubjects([]);
      navigate(`/payment-success?paymentId=${encodeURIComponent(payment.id)}`, {
        replace: true,
        state: { paymentId: payment.id, planLabel },
      });
    } catch (err) {
      throw err;
    } finally {
      setBusy(false);
    }
  }

  if (checkout) {
    const summaryLines =
      checkout.kind === 'exam'
        ? [
            { label: 'Type', value: 'GED Exam Fee' },
            { label: 'Subjects', value: String(checkout.subjects?.length || 0) },
            ...(checkout.breakdown?.items || []).map((item) => ({
              label: item.name,
              value: formatUsd(item.amount),
            })),
            {
              label: 'Service charges & taxes',
              value: formatUsd(checkout.breakdown?.serviceAndTax || 0),
            },
          ]
        : [
            { label: 'Type', value: 'Degree / Diploma' },
            ...(checkout.breakdown?.items || []).map((item) => ({
              label: item.name,
              value: formatUsd(item.amount),
            })),
            {
              label: 'Service charges & taxes',
              value: formatUsd(checkout.breakdown?.serviceAndTax || 0),
            },
          ];

    return (
      <AppShell navItems={navItems}>
        <div style={s.wrap}>
          <PaymentGateway
            amount={checkout.amount}
            title={checkout.kind === 'exam' ? 'Exam fee checkout' : 'Degree charges checkout'}
            payLabel={busy ? 'Processing…' : `Pay ${formatUsd(checkout.amount)}`}
            summaryLines={summaryLines}
            onPay={handleCheckoutPay}
            onCancel={() => navigate('/payment-failed?reason=cancelled', { replace: true })}
            onFail={() => navigate('/payment-failed?reason=failed', { replace: true })}
          />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell navItems={navItems}>
      <div style={s.wrap}>
        <div style={s.hero}>
          <p style={s.eyebrow}>Payments</p>
          <h1 style={s.title}>Your payment center</h1>
          <p style={s.sub}>
            View all payments, pay GED exam fees by subject, and order diploma &amp; transcript
            charges. Bank checkout creates an order reference only — card and customer details are not saved.
          </p>
        </div>

        {contactMsg ? <div style={s.infoBanner}>{contactMsg}</div> : null}

        {/* My payments table */}
        <section style={s.section}>
          <div style={s.sectionHead}>
            <h2 style={s.sectionTitle}>My payments</h2>
            <input
              style={s.search}
              placeholder="Search alphabetically (label, reference, mode…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <PaymentTable
            rows={allSorted}
            empty="No payments yet."
            onViewReceipt={setReceiptPayment}
          />
        </section>

        {/* Exam fee */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>GED Exam fee</h2>
          <p style={s.hint}>
            {formatUsd(examFee.perSubjectUsd)} per subject exam fee + {formatUsd(examFee.serviceAndTaxUsd)}{' '}
            service charges &amp; taxes <strong>for each</strong> of the 4 GED exams (editable by admin).
            All 4 subjects = {formatUsd(computeExamCheckoutTotal(4, examFee).total)}.
          </p>
          {pricingLoading ? <p>Loading fee structure…</p> : null}
          <div style={s.subjectGrid}>
            {GED_EXAM_SUBJECTS.map((subj) => {
              const on = selectedSubjects.includes(subj.id);
              return (
                <button
                  key={subj.id}
                  type="button"
                  onClick={() => toggleSubject(subj.id)}
                  style={{
                    ...s.subjectCard,
                    borderColor: on ? SBA.gold : '#e2e8f0',
                    background: on ? 'rgba(197,160,70,0.08)' : '#fff',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ color: on ? SBA.gold : '#94a3b8' }}>
                    {on ? 'check_circle' : 'radio_button_unchecked'}
                  </span>
                  <span>{subj.name}</span>
                  <small style={s.subjectPrice}>
                    {formatUsd(examFee.perSubjectUsd + examFee.serviceAndTaxUsd)}
                  </small>
                </button>
              );
            })}
          </div>
          <div style={s.totalRow}>
            <span>
              Selected: {selectedSubjects.length} · Exam {formatUsd(examTotals.examSubtotal)} + Tax{' '}
              {formatUsd(examTotals.taxSubtotal)}
            </span>
            <strong>Total {formatUsd(examTotals.total)}</strong>
          </div>
          <ModePicker value={examMode} onChange={setExamMode} />
          <button type="button" style={s.primaryBtn} onClick={handleExamContinue}>
            Continue with exam fee
          </button>

          <div style={{ ...s.sectionHead, marginTop: 28 }}>
            <h3 style={s.subHead}>Student exam fee payments</h3>
            <input
              style={s.search}
              placeholder="Search exam fee payments…"
              value={examSearch}
              onChange={(e) => setExamSearch(e.target.value)}
            />
          </div>
          <PaymentTable
            rows={examPayments}
            empty="No exam fee payments yet."
            onViewReceipt={setReceiptPayment}
          />
        </section>

        {/* Degree */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>GED Course degree charges</h2>
          <p style={s.hint}>
            Diploma and transcript: {formatUsd(degreeFee.diplomaTranscriptUsd)} + service charges &amp;
            taxes {formatUsd(degreeFee.serviceAndTaxUsd)} (both editable by admin). Total{' '}
            {formatUsd(degreeTotals.total)}.
          </p>
          <ModePicker value={degreeMode} onChange={setDegreeMode} />
          <button type="button" style={s.primaryBtn} onClick={handleDegreeContinue}>
            Order diploma &amp; transcript — {formatUsd(degreeTotals.total)}
          </button>
        </section>

        {receiptPayment ? (
          <div style={s.modalOverlay} onClick={() => setReceiptPayment(null)}>
            <div style={s.modal} onClick={(e) => e.stopPropagation()}>
              <div style={s.modalTop}>
                <h3 style={{ margin: 0, color: SBA.navy }}>Receipt</h3>
                <button type="button" style={s.closeBtn} onClick={() => setReceiptPayment(null)}>
                  Close
                </button>
              </div>
              <PaymentReceipt payment={receiptPayment} />
              <Link
                to={`/payment-confirmation/${receiptPayment.id}`}
                style={{ ...s.link, display: 'inline-block', marginTop: 12 }}
              >
                Open confirmation page
              </Link>
            </div>
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}

function ModePicker({ value, onChange }) {
  const modes = [
    { id: 'card', label: 'Card' },
    { id: 'online', label: 'Online banking' },
    { id: 'cash', label: 'Cash' },
  ];
  return (
    <div style={s.modeRow}>
      <span style={s.modeLabel}>Payment mode</span>
      {modes.map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => onChange(m.id)}
          style={{
            ...s.modeBtn,
            background: value === m.id ? SBA.navy : '#fff',
            color: value === m.id ? '#fff' : SBA.navy,
            borderColor: value === m.id ? SBA.navy : '#e2e8f0',
          }}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function PaymentTable({ rows, empty, onViewReceipt }) {
  if (!rows.length) {
    return <p style={s.empty}>{empty}</p>;
  }
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Date</th>
            <th style={s.th}>Reference</th>
            <th style={s.th}>Category</th>
            <th style={s.th}>Label</th>
            <th style={s.th}>Amount</th>
            <th style={s.th}>Mode</th>
            <th style={s.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id}>
              <td style={s.td}>{formatPaymentDate(p)}</td>
              <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: 700 }}>{p.referenceNumber}</td>
              <td style={s.td}>{categoryLabel(p.category)}</td>
              <td style={s.td}>{p.label}</td>
              <td style={s.td}>{formatUsd(p.amount)}</td>
              <td style={s.td}>{modeLabel(p.mode)}</td>
              <td style={s.td}>
                <button type="button" style={s.linkBtn} onClick={() => onViewReceipt(p)}>
                  Invoice
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const s = {
  wrap: { maxWidth: 1100, margin: '0 auto', padding: '24px 18px 56px' },
  hero: { marginBottom: 22 },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: SBA.goldStrong,
  },
  title: { margin: '6px 0 8px', fontSize: 28, fontWeight: 800, color: SBA.navy },
  sub: { margin: 0, color: '#475569', fontSize: 14, maxWidth: 720, lineHeight: 1.55 },
  section: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 18,
    padding: '22px 20px',
    marginBottom: 18,
    boxShadow: '0 8px 24px rgba(27,54,93,0.06)',
  },
  sectionHead: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  sectionTitle: { margin: '0 0 10px', fontSize: 18, fontWeight: 800, color: SBA.navy },
  subHead: { margin: 0, fontSize: 15, fontWeight: 800, color: SBA.navy },
  hint: { margin: '0 0 14px', fontSize: 13, color: '#64748b', lineHeight: 1.5 },
  search: {
    minWidth: 240,
    flex: 1,
    maxWidth: 360,
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 13,
  },
  subjectGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 10,
    marginBottom: 14,
  },
  subjectCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 14px',
    borderRadius: 12,
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    cursor: 'pointer',
    textAlign: 'left',
    fontWeight: 700,
    fontSize: 13,
    color: SBA.navy,
  },
  subjectPrice: { marginLeft: 'auto', color: '#64748b', fontWeight: 600 },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    padding: '12px 14px',
    borderRadius: 12,
    background: 'rgba(27,54,93,0.05)',
    marginBottom: 14,
    fontSize: 13,
    color: '#334155',
  },
  modeRow: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 14 },
  modeLabel: { fontSize: 12, fontWeight: 800, color: '#64748b', marginRight: 4 },
  modeBtn: {
    padding: '8px 12px',
    borderRadius: 999,
    border: '1.5px solid #e2e8f0',
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: '12px 18px',
    borderRadius: 12,
    border: 'none',
    background: SBA.navy,
    color: '#fff',
    fontWeight: 800,
    fontSize: 14,
    cursor: 'pointer',
  },
  infoBanner: {
    marginBottom: 16,
    padding: '12px 14px',
    borderRadius: 12,
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    color: '#9a3412',
    fontWeight: 600,
    fontSize: 13,
  },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left',
    padding: '10px 8px',
    borderBottom: '2px solid #e2e8f0',
    color: '#64748b',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  td: { padding: '10px 8px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', verticalAlign: 'top' },
  empty: { margin: 0, color: '#94a3b8', fontSize: 13 },
  linkBtn: {
    border: 'none',
    background: 'transparent',
    color: SBA.navy,
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  },
  link: { color: SBA.navy, fontWeight: 700, fontSize: 13 },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.45)',
    zIndex: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    maxWidth: 720,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: 20,
  },
  modalTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  closeBtn: {
    border: '1px solid #e2e8f0',
    background: '#fff',
    borderRadius: 8,
    padding: '6px 10px',
    fontWeight: 700,
    cursor: 'pointer',
  },
};
